import ApiClient from 'api-client/ApiClient'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import ActionsSet from 'core/actions/ActionsSet'
import ListAction from 'core/actions/ListAction'
import DeleteAction from 'core/actions/DeleteAction'
import CustomAction from 'core/actions/CustomAction'
import CreateAction from 'core/actions/CreateAction'
import UpdateAction from 'core/actions/UpdateAction'
import store from 'app/store'
import { cacheActions } from 'core/caching/cacheReducers'
import { noop } from 'utils/fp'
import moment from 'moment'
import { sessionActions } from 'core/session/sessionReducers'
import { generateApiErrorPayload } from 'api-client/helpers'

type CacheableRecordArray = Record<string, unknown>[]

const { dispatch } = store
const { cinder, nova } = ApiClient.getInstance()

const SIMPLIFIED_VOLUME_FIELDS = [
  'name',
  'id',
  'description',
  'status',
  'size',
  'bootable',
  'volume_type',
  // TODO: Remove fecthing attachments from volume list API
  // call and replace with attachedVms field
  'attachments',
  'attachedVms',
  'multiattach',
  'volume_image_metadata',
]

const getCurrentCinderFormattedTimestamp = () => {
  return (
    moment()
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss') + 'Z'
  )
}

export const allVolumeActions = ActionsSet.make<DataKeys.AllOpenstackVolumes>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.AllOpenstackVolumes,
  cacheKey: DataKeys.AllOpenstackVolumes,
})

export const listAllVolumes = allVolumeActions.add(
  new ListAction<DataKeys.AllOpenstackVolumes>(async () => {
    const newTimestamp = getCurrentCinderFormattedTimestamp()
    dispatch(
      sessionActions.updateSession({
        volumesLastPolledTimestamp: newTimestamp,
      }),
    )

    // Only load the necessary fields here
    const volumes = await cinder.getPf9VolumesWithParams(
      'all_tenants=true&fields=id,name,hostId,tenantName',
    )
    return volumes
  }),
)

export const getVolumeById = async ({ id }) => {
  const result = await cinder.getPf9Volume({ id })
  dispatch(
    cacheActions.updateItem({
      uniqueIdentifier: 'id',
      cacheKey: DataKeys.NonPaginatedOpenstackVolumes,
      params: { id },
      item: {
        ...result,
        attachedVms: result.attachedVms || [],
      },
    }),
  )
  return result
}

export const volumeActions = ActionsSet.make<DataKeys.OpenstackVolumes>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackVolumes,
  cacheKey: DataKeys.OpenstackVolumes,
  addCacheKey: DataKeys.AllOpenstackVolumes,
})

export const listVolumes = volumeActions.add(
  new ListAction<DataKeys.OpenstackVolumes, { loadAllVolumes?: boolean }>(async () => {
    const newTimestamp = getCurrentCinderFormattedTimestamp()
    dispatch(
      sessionActions.updateSession({
        volumesLastPolledTimestamp: newTimestamp,
      }),
    )
    const { volumes, volume_counts } = await cinder.getPf9Volumes()
    // not much to do with volume_counts at the moment...
    return volumes
  }),
)

export const createVolume = volumeActions.add(
  new CreateAction<DataKeys.OpenstackVolumes, { body }>(async ({ body }) => {
    const response = await cinder.createVolume(body)
    if (response?.overLimit) {
      return response
    }
    const volumeId = response?.id
    if (!volumeId) {
      const errorPayload = generateApiErrorPayload(response, '', 'Failed to create volume')
      throw errorPayload?.raw || errorPayload || new Error('Failed to create volume')
    }
    const newVolume = await cinder.getPf9Volume({ id: volumeId })
    return newVolume
  }),
)

export const updateVolume = volumeActions.add(
  new UpdateAction<DataKeys.OpenstackVolumes, { id: string; body: any }>(async ({ id, body }) => {
    const updatedVolume = await cinder.updateVolume(id, body)
    return updatedVolume
  }),
)

export const deleteVolume = volumeActions.add(
  new CustomAction<
    DataKeys.OpenstackVolumes,
    { id: string; isForceCascade?: boolean; isCascade?: boolean }
  >(
    'deleteVolume',
    async ({ id, isForceCascade, isCascade }) => {
      try {
        await cinder.deleteVolume(id, isForceCascade, isCascade)
        const volume = await cinder.getVolume(id)
        return volume
      } catch (error) {
        if (error?.status === 404) {
          // Volume is already deleted
          return { id, status: 'deleted' }
        }
        throw error
      }
    },
    async (result, { id }) => {
      // Update the volume in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id },
          item: result,
        }),
      )

      //Remove the item from AvailableOpenstackVolumes and NonPaginatedOpenstackVolumes
      //Placing in try-catch block to handle any runtime error
      try {
        dispatch(
          cacheActions.removeItem({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.AvailableOpenstackVolumes,
            params: { id },
          }),
        )
        dispatch(
          cacheActions.removeItem({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.NonPaginatedOpenstackVolumes,
            params: { id },
          }),
        )
      } catch (error) {
        // Placeholder to handle any runtime error
        console.error('Error removing volume from caches:', error)
      }
    },
  ),
)

export const refreshVolume = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string }>(
    'volumeRefresh',
    async ({ id }) => {
      const volume = await cinder.getPf9Volume({ id })
      return volume
    },
    (result, { id }) => {
      // Update the volume in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const attachVolume = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string; vmId: string; body: any }>(
    'attachVolume',
    async ({ id, vmId, body }) => {
      await nova.attachVolume({ vmId, body })
      const volume = await cinder.getPf9Volume({ id })
      return volume
    },
    async (result, { id, vmId }) => {
      // Update the vm and volume in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.NonPaginatedOpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
      const vm = await nova.getInstance(vmId)
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id: vmId }, // TODO: Double check this works
          item: (vm as unknown) as Record<string, unknown>,
        }),
      )
    },
  ),
)

export const detachVolume = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { volumeId: string; vmId: string }>(
    'detachVolume',
    async ({ volumeId, vmId }) => {
      await nova.detachVolume({ volumeId, vmId })
      const volume = await cinder.getPf9Volume({ id: volumeId })
      return volume
    },
    async (result, { volumeId, vmId }) => {
      // Update the vm and volume in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.NonPaginatedOpenstackVolumes,
          params: { id: volumeId }, // TODO: Double check this works
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id: volumeId }, // TODO: Double check this works
          item: result,
        }),
      )
      const vm = await nova.getInstance(vmId)
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id: vmId }, // TODO: Double check this works
          item: (vm as unknown) as Record<string, unknown>,
        }),
      )
    },
  ),
)

export const uploadVolumeAsImage = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string; body: any }>(
    'uploadVolumeAsImage',
    async ({ id, body }) => {
      const response = await cinder.uploadVolumeAsImage({ id, body })
      return response
    },
    async (result, { id }) => {
      noop()
    },
  ),
)

export const bootableVolume = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string; isBootable: boolean }>(
    'setBootable',
    async ({ id, isBootable }) => {
      await cinder.setBootable(id, isBootable)
      const volume = await cinder.getPf9Volume({ id })
      return volume
    },
    async (result, { id }) => {
      // Update the vm and volume in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

export const extendVolume = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { volume: any; size: any }>(
    'extendVolume',
    async ({ volume, size }) => {
      await cinder.extendVolume(volume?.id, size)
      const updatedVolume = await cinder.getPf9Volume({ id: volume?.id })
      return updatedVolume
    },
    async (result, { volume }) => {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id: volume?.id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id: volume?.id },
          item: result,
        }),
      )
    },
  ),
)

export const retypeVolume = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string; body: any }>(
    'retypeVolume',
    async ({ id, body }) => {
      await cinder.retypeVolume({ id, body })
      const updatedVolume = await cinder.getPf9Volume({ id })
      return updatedVolume
    },
    async (result, { id }) => {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const migrateVolume = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string; body: any }>(
    'migrateVolume',
    async ({ id, body }) => {
      await cinder.migrateVolume({ id, body })
      const updatedVolume = await cinder.getPf9Volume({ id })
      return updatedVolume
    },
    async (result, { id }) => {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const updateVolumeMetadata = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string; body?: any }>(
    'updateVolumeMetadata',
    async ({ id, body }) => {
      await cinder.setVolumeMetadata({ id, body })
      const volume = await cinder.getPf9Volume({ id })
      return volume
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

// add action to set volume state
export const setVolumeState = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes, { id: string; body: any }>(
    'setVolumeState',
    async ({ id, body }) => {
      await cinder.resetVolumeStatus(id, body)
      const volume = await cinder.getPf9Volume({ id })
      return volume
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

/**
 * Parameters for polling volumes - same as pagination params
 */

export const pollVolumes = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes>(
    'pollVolumes',
    async (params: ListVolumesNextPageParams = {}) => {
      const state = store.getState()
      const lastTimestamp = state.session.volumesLastPolledTimestamp
      if (!lastTimestamp) {
        return []
      }
      const newTimestamp = getCurrentCinderFormattedTimestamp()
      dispatch(
        sessionActions.updateSession({
          volumesLastPolledTimestamp: newTimestamp,
        }),
      )
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.OpenstackVolumes,
          lastRefreshTime: new Date().getTime(),
        }),
      )

      // Build query params for pagination, search, filter, and sort
      const queryParams = buildQueryParams(params)
      const volumes = await cinder.getPf9VolumesSince(lastTimestamp, queryParams)
      return volumes
    },
    (result, {}) => {
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          items: result,
        }),
      )
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          items: result,
        }),
      )
    },
  ),
)

/**
 * Get total count of volumes for a network (supports search/filter params for filtered counts)
 */
export interface GetVolumesCountParams {
  searchField?: string
  searchValue?: string
  filters?: Record<string, string>
}

export const getVolumesCount = async (
  allTenants: boolean,
  params?: GetVolumesCountParams,
): Promise<number> => {
  // Build query params for filtering
  const queryParams: string[] = []

  // All tenants param
  if (allTenants) {
    queryParams.push(`all_tenants=true`)
  }

  // Search params
  if (params?.searchField && params?.searchValue) {
    queryParams.push(
      `search_key=${params.searchField}&search=${encodeURIComponent(params.searchValue)}`,
    )
  }

  // Filter params
  if (params?.filters) {
    Object.entries(params.filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        queryParams.push(`${key}=${encodeURIComponent(value)}`)
      }
    })
  }

  return cinder.getPf9VolumesCount(queryParams.join('&'))
}

/**
 * Lightweight pagination action for volumes - supports limit/marker pagination
 * with search, filter, and sort capabilities.
 * Appends to cache on subsequent pages, replaces on first page.
 */
interface ListVolumesNextPageParams {
  allTenants?: boolean
  limit?: number
  marker?: string
  // Sort params
  sort_key?: string
  sort_dir?: 'asc' | 'desc'
  // Search params
  search_field?: string
  search_value?: string
  // Filter params
  filters?: Record<string, string>
}

// Build query params for pagination, search, filter, and sort
export const buildQueryParams = (params: ListVolumesNextPageParams) => {
  const {
    allTenants = false,
    limit = 10,
    marker,
    sort_key,
    sort_dir,
    search_field,
    search_value,
    filters,
  } = params || {}

  // Build query params for pagination, search, filter, and sort
  const queryParams: string[] = []
  if (allTenants) {
    queryParams.push(`all_tenants=true`)
  }
  if (limit) {
    queryParams.push(`limit=${limit}`)
  }
  if (marker) {
    queryParams.push(`marker=${marker}`)
  }
  // Sort params
  if (sort_key) {
    queryParams.push(`sort_key=${sort_key}`)
  }
  if (sort_dir) {
    queryParams.push(`sort_dir=${sort_dir}`)
  }
  // Search params - map search field to API query param
  if (search_field && search_value) {
    queryParams.push(`search_key=${search_field}&search=${encodeURIComponent(search_value)}`)
  }
  // Filter params
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        queryParams.push(`${key}=${encodeURIComponent(value)}`)
      }
    })
  }

  return queryParams.join('&')
}

export const listVolumesNextPage = volumeActions.add(
  new CustomAction<DataKeys.OpenstackVolumes>(
    'listNextPage',
    async (params: ListVolumesNextPageParams) => {
      const { limit = 10, marker } = params || {}

      // Update last polled timestamp
      dispatch(
        sessionActions.updateSession({
          volumesLastPolledTimestamp: getCurrentCinderFormattedTimestamp(),
        }),
      )

      const queryParams = buildQueryParams(params)
      const volumes = await cinder.getPf9VolumesWithParams(queryParams)

      // Note: Count is fetched separately by the hook via countAction
      // to avoid duplicate API calls. We only update pagination metadata here.

      // Calculate pagination metadata
      const nextMarker =
        volumes?.length === limit && volumes?.length > 0 ? volumes[volumes.length - 1].id : null
      const state = store.getState()
      const currentPagination = (state.session?.pagination as Record<string, any>) || {}
      const existingMeta = currentPagination?.[DataKeys.OpenstackVolumes] || {}
      const totalCount = existingMeta?.totalCount // Use existing count from session
      const currentLoadedCount = state.cache?.cachedData?.[DataKeys.OpenstackVolumes]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount + volumes.length : volumes.length
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : volumes?.length === limit

      // Store pagination metadata in session
      dispatch(
        sessionActions.updateSession({
          pagination: {
            ...currentPagination,
            [DataKeys.OpenstackVolumes]: {
              ...existingMeta,
              currentMarker: marker || null,
              nextMarker,
              hasMore,
              pageSize: limit,
            },
          },
        } as any),
      )

      return volumes
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: any[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.OpenstackVolumes,
            items: (result as unknown) as CacheableRecordArray,
          }),
        )
      } else {
        // First page (no marker) - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.OpenstackVolumes,
            items: (result as unknown) as CacheableRecordArray,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.OpenstackVolumes,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

export const allVolumeTransferActions = ActionsSet.make<DataKeys.AllVolumeTransfers>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.AllVolumeTransfers,
  cacheKey: DataKeys.AllVolumeTransfers,
})

export const listAllVolumeTransfers = allVolumeTransferActions.add(
  new ListAction<DataKeys.AllVolumeTransfers>(async () => {
    return cinder.getAllVolumeTransfers()
  }),
)

export const volumeTransferActions = ActionsSet.make<DataKeys.VolumeTransfers>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.VolumeTransfers,
  cacheKey: DataKeys.VolumeTransfers,
  addCacheKey: DataKeys.AllVolumeTransfers,
})

export const listVolumeTransfers = volumeTransferActions.add(
  new ListAction<DataKeys.VolumeTransfers>(async () => {
    return cinder.getVolumeTransfers()
  }),
)

export const createVolumeTransfer = volumeTransferActions.add(
  new CreateAction<DataKeys.VolumeTransfers, { body: any; volumeId: string }>(
    async ({ body, volumeId }) => {
      const created = await cinder.createVolumeTransfer({ body })
      const volume = await cinder.getPf9Volume({ id: volumeId })
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id: volumeId },
          item: volume,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id: volumeId },
          item: volume,
        }),
      )
      return created
    },
  ),
)

export const deleteVolumeTransfer = volumeTransferActions.add(
  new DeleteAction<DataKeys.VolumeTransfers, { id: string; volumeId: string }>(
    async ({ id, volumeId }) => {
      await cinder.deleteVolumeTransfer(id)
      const volume = await cinder.getPf9Volume({ id: volumeId })
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVolumes,
          params: { id: volumeId },
          item: volume,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVolumes,
          params: { id: volumeId },
          item: volume,
        }),
      )
    },
  ),
)

export const acceptVolumeTransfer = volumeTransferActions.add(
  new CustomAction<DataKeys.VolumeTransfers, { id: string; body: any }>(
    'attachVolume',
    async ({ id, body }) => {
      const response = await cinder.acceptVolumeTransfer({ id, body })
      return response
    },
    async (result, { id }) => {
      // Update the vm and volume in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.VolumeTransfers,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllVolumeTransfers,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

export const simpleVolumeActions = ActionsSet.make<DataKeys.OpenstackVolumesLite>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackVolumesLite,
  cacheKey: DataKeys.OpenstackVolumesLite,
})

export const listSimpleVolumes = simpleVolumeActions.add(
  new ListAction<DataKeys.OpenstackVolumesLite>(async () => {
    return cinder.getSimpleVolumes()
  }),
)

export const singleVolumeActions = ActionsSet.make<DataKeys.SingleOpenstackVolume>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.SingleOpenstackVolume,
  cacheKey: DataKeys.SingleOpenstackVolume,
})

export const listSingleVolume = singleVolumeActions.add(
  new ListAction<DataKeys.SingleOpenstackVolume, { id: string }>(async ({ id }) => {
    const volume = await cinder.getPf9Volume({ id })
    return [volume]
  }),
)

export const updateSingleVolume = singleVolumeActions.add(
  new CustomAction<DataKeys.SingleOpenstackVolume, { id: string }>(
    'updateSingleVolume',
    async ({ id }) => {
      const volume = await cinder.getPf9Volume({ id })
      const snapshots = await cinder.getSnapshotsForVolume(id)
      return [
        {
          ...volume,
          snapshots: [...snapshots],
        },
      ]
    },
    (result: any[], { id }) => {
      // Update the volume in the cache
      dispatch(
        cacheActions.replaceAll({
          cacheKey: DataKeys.SingleOpenstackVolume,
          items: result,
        }),
      )
    },
  ),
)

export const nonPaginatedVolumeActions = ActionsSet.make<DataKeys.NonPaginatedOpenstackVolumes>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.NonPaginatedOpenstackVolumes,
  cacheKey: DataKeys.NonPaginatedOpenstackVolumes,
})

// A simplified smaller return for usage in forms & places where
// aggregate data is required
export const listNonPaginatedVolumes = nonPaginatedVolumeActions.add(
  new ListAction<DataKeys.NonPaginatedOpenstackVolumes>(async () => {
    dispatch(
      sessionActions.updateSession({
        nonPaginatedVolumesLastPolledTimestamp: getCurrentCinderFormattedTimestamp(),
      }),
    )
    const params = `fields=${SIMPLIFIED_VOLUME_FIELDS.join(',')}`
    const volumes = await cinder.getPf9VolumesWithParams(params)
    return volumes
  }),
)

export const pollNonPaginatedVolumes = nonPaginatedVolumeActions.add(
  new CustomAction<DataKeys.NonPaginatedOpenstackVolumes>(
    'pollNonPaginatedVolumes',
    async ({ loadAllVolumes = false }: { loadAllVolumes?: boolean } = {}) => {
      const state = store.getState()
      const lastTimestamp = state.session.nonPaginatedVolumesLastPolledTimestamp
      dispatch(
        sessionActions.updateSession({
          nonPaginatedVolumesLastPolledTimestamp: getCurrentCinderFormattedTimestamp(),
        }),
      )
      const volumes = await cinder.getPf9VolumesWithParams(
        (lastTimestamp && !loadAllVolumes ? `updated_at=gt:${lastTimestamp}&` : '') +
          `fields=${SIMPLIFIED_VOLUME_FIELDS.join(',')}`,
      )
      return volumes
    },
    (result: any) => {
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.NonPaginatedOpenstackVolumes,
          items: (result as unknown) as CacheableRecordArray,
        }),
      )
    },
  ),
)

// Available volumes are volumes that are not attached to any instance
export const availableVolumeActions = ActionsSet.make<DataKeys.AvailableOpenstackVolumes>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.AvailableOpenstackVolumes,
  cacheKey: DataKeys.AvailableOpenstackVolumes,
})

export const listAvailableVolumes = availableVolumeActions.add(
  new ListAction<DataKeys.AvailableOpenstackVolumes>(async () => {
    const { volumes } = await cinder.getAvailableVolumes(false, null)
    return volumes
  }),
)

export const pollAvailableVolumes = availableVolumeActions.add(
  new CustomAction<DataKeys.AvailableOpenstackVolumes>(
    'pollAvailableVolumes',
    async () => {
      const state = store.getState()
      const lastTimestamp = state.session.availableVolumesLastPolledTimestamp
      dispatch(
        sessionActions.updateSession({
          availableVolumesLastPolledTimestamp: getCurrentCinderFormattedTimestamp(),
        }),
      )
      const { volumes } = await cinder.getAvailableVolumes(false, lastTimestamp)
      return volumes
    },
    (result: any) => {
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AvailableOpenstackVolumes,
          items: (result as unknown) as CacheableRecordArray,
        }),
      )
    },
  ),
)

// Not sure if we should make a custom action for this, bc what if we
// are loading a volume on a different tenant?
export const getSingleVolume = (id) => {
  return cinder.getPf9Volume({ id })
}

export const getVolumesSince = (timestamp) => {
  return cinder.getPf9VolumesSince(timestamp)
}
